import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { AiActions } from '../../Services/_actions';
import { userInformationAtom } from '../../Services/_states/auth';

export const useKnowledgeMapSubjects = (
    shouldFetch = false,
    externalData?: {
        knowledgeMapData?: any[];
        entityData?: any;
    },
    options?: {
        fetchKnowledgeMap?: boolean;
    }
) => {
    const [subjects, setSubjects] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [knowledgeMapData, setKnowledgeMapData] = useState<any[]>([]);
    const [data, setData] = useState<any>(null);
    const [studentClass, setStudentClass] = useState<number | null>(null);

    const loggedInUser = useRecoilValue(userInformationAtom);
    const mobile = loggedInUser?.mobile;
    const loggedInUsername = loggedInUser?.username || '';
    const isSuperuser = loggedInUser?.is_superuser === true;

    const actions = AiActions();

    const shouldFetchKnowledgeMap = options?.fetchKnowledgeMap ?? true;

    const getEntityRecord = async (mobileNumber?: string) => {
        if (!mobileNumber) return;
        try {
            const response = await actions.getRecordEntity(mobileNumber, {
                template_form_type: 'contact_form',
            });
            setData(response);
        } catch (error) {
            console.error('❌ Failed to get entity record', error);
        }
    };

    useEffect(() => {
        if (externalData?.entityData !== undefined) {
            setData(externalData.entityData);
            return;
        }
        if (!shouldFetch) return;
        getEntityRecord(mobile);
    }, [mobile, shouldFetch, externalData?.entityData]);

    useEffect(() => {
        if (!data || !loggedInUsername) return;
        const matchedItem = data?.items?.find(
            (item: any) => item?.entity?.username === loggedInUsername
        );
        if (matchedItem) {
            setStudentClass(matchedItem?.entity?.class);
        }
    }, [data, loggedInUsername]);

    const getOrganisationId = (): string | undefined => {
        try {
            const raw = localStorage?.getItem('userInfo');
            if (!raw) return undefined;
            const parsed = JSON?.parse(raw);
            return parsed?.profile?.organisation?.organisation_id;
        } catch {
            return undefined;
        }
    };

    // ─── Knowledge Map ────────────────────────────────────────────────────────────
    const getKnowledgeMap = async () => {
        try {
            setLoading(true);
            const organisationId = isSuperuser ? undefined : getOrganisationId();
            const response = await actions.getKnowledgeList(organisationId);
            setKnowledgeMapData(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('❌ Knowledge Map API error:', error);
            setKnowledgeMapData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (externalData?.knowledgeMapData !== undefined) {
            setKnowledgeMapData(
                Array.isArray(externalData.knowledgeMapData) ? externalData.knowledgeMapData : []
            );
            return;
        }
        if (!shouldFetchKnowledgeMap) {
            setKnowledgeMapData([]);
            return;
        }
        if (!shouldFetch) return;
        getKnowledgeMap();
    }, [shouldFetch, externalData?.knowledgeMapData, shouldFetchKnowledgeMap]);

    // ─── Filtered KnowledgeMap by student class ───────────────────────────────────
    const filteredKnowledgeMap = useMemo(() => {
        if (!knowledgeMapData.length || !studentClass) return [];
        const studentClassNum = Number(studentClass);
        return knowledgeMapData.filter((item: any) => {
            if (!item.class) return false;
            if (item.class.includes('-')) {
                const [start, end] = item.class.split('-').map((v: string) => Number(v.trim()));
                return studentClassNum >= start && studentClassNum <= end;
            }
            return Number(item.class) === studentClassNum;
        });
    }, [knowledgeMapData, studentClass]);

    // ─── Subject Hierarchy ────────────────────────────────────────────────────────
    const subjectHierarchy = useMemo(() => {
        const hierarchy: Record<string, Record<string, string[]>> = {};
        filteredKnowledgeMap.forEach((subjectData: any) => {
            if (!hierarchy[subjectData.subject]) {
                hierarchy[subjectData.subject] = {};
            }
            subjectData.topics?.forEach((topic: any) => {
                hierarchy[subjectData.subject][topic.topic] =
                    topic.subtopics?.map((s: any) => s.subtopic) ?? [];
            });
        });
        return hierarchy;
    }, [filteredKnowledgeMap]);

    // ─── Subjects list ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!studentClass || !knowledgeMapData.length) return;
        const classStr = String(studentClass);
        const filtered = knowledgeMapData
            .filter((item: any) => item?.class === classStr)
            .map((item: any) => ({ label: item.subject, value: item.subject, raw: item }));
        setSubjects(filtered);
    }, [studentClass, knowledgeMapData]);

    // ─── Available subjects (string[]) ───────────────────────────────────────────
    const availableSubjectsFromClass = useMemo<string[]>(() => {
        return [...new Set(filteredKnowledgeMap.map((item: any) => item.subject))];
    }, [filteredKnowledgeMap]);

    return {
        subjects,
        loading,
        subjectHierarchy,
        availableSubjectsFromClass,
        knowledgeMapData,
        entityData: data,
    };
};

